import { useAuth0 } from "ct-auth"
import { Box, CircularProgress, Typography } from "@mui/material"
import { useCallback, useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { authCapabilities } from "../../context/auth/runtime-config"
import { resolveInviteToken, peekInviteToken } from "../../data-utils/data"
import EmailCodeSignIn from "../User/Login/EmailCodeSignIn.jsx"
import { encodeRFC3986URIComponent } from "../../data-utils/uiUtils"
import { ROUTE_PATHS } from "../../utilities/constants"
import logger from "../../Logger"

const pageLogger = logger.child({ module: "invite-handler" })

export function InviteHandler() {
  const { isAuthenticated, isLoading, loginWithRedirect, logout, getAccessTokenSilently, user } = useAuth0()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Set when the invitee must sign in with an emailed code rather than at the IdP.
  const [emailCodeFor, setEmailCodeFor] = useState(null)
  // If Auth0 returned an error (e.g. access_denied from SSO gate), go to login page
  const authError = searchParams.get("error")
  // Token now travels in the ?token= query string (was a path param). The
  // route is registered as plain "/invite" so the path can be allow-listed
  // verbatim in Auth0's returnTo settings.
  const tokenId = searchParams.get("token")

  useEffect(() => {
    if (isLoading) return

    if (authError) {
      pageLogger.warn(`Auth0 returned error '${authError}', redirecting to login`)
      navigate(ROUTE_PATHS.ROOT)
      return
    }

    if (!tokenId) {
      pageLogger.warn("No token in query string, redirecting to login")
      navigate(ROUTE_PATHS.ROOT)
      return
    }

    const handleInvite = async () => {
      // Peek the token to get the intended player's email and auth method
      const peekResult = await peekInviteToken({ tokenId })
      const invitedEmail = peekResult?.email
      const authHint = peekResult?.authHint || "email"

      if (isAuthenticated) {
        // If logged in as a different user, log out and come back
        if (invitedEmail && user?.email?.toLowerCase() !== invitedEmail.toLowerCase()) {
          pageLogger.info(`Logged in as '${user?.email}' but invite is for '${invitedEmail}', logging out`)
          logout({
            logoutParams: {
              returnTo: `${window.location.origin}${process.env.PUBLIC_URL ?? "/"}/invite?token=${encodeURIComponent(tokenId)}`,
            },
          })
          return
        }

        // Correct user — resolve the token
        try {
          const accessToken = await getAccessTokenSilently()
          const { result, status } = await resolveInviteToken(accessToken, user.sub, { tokenId })

          if (status === 200 && result?.sessionName) {
            pageLogger.info(`Invite resolved: session='${result.sessionName}'`)
            sessionStorage.removeItem(`ct_invite_redirect_${tokenId}`)
            navigate(`${ROUTE_PATHS.GAME_ROOT}/${encodeRFC3986URIComponent(result.sessionName)}`)
          } else {
            pageLogger.warn(`Invite token failed (status: ${status}), redirecting to games`)
            navigate(ROUTE_PATHS.GAME_ROOT)
          }
        } catch (err) {
          pageLogger.error(`Failed to resolve invite token: ${err}`)
          navigate(ROUTE_PATHS.GAME_ROOT)
        }
        return
      }

      // Not authenticated — redirect to Auth0 login (only once to prevent loops).
      // useRef won't survive the full-page Auth0 redirect, so persist the flag in sessionStorage.
      const REDIRECT_FLAG = `ct_invite_redirect_${tokenId}`
      if (sessionStorage.getItem(REDIRECT_FLAG)) {
        pageLogger.warn("Login redirect already attempted, going to login page")
        sessionStorage.removeItem(REDIRECT_FLAG)
        navigate(ROUTE_PATHS.ROOT)
        return
      }
      sessionStorage.setItem(REDIRECT_FLAG, "1")

      // A passwordless invitee has NO credential at the identity provider — on Auth0
      // the `email` connection handled that, and Keycloak has no equivalent (it has no
      // email-OTP authenticator at all). Redirecting there anyway lands them on a
      // password form for an account with no password, which Keycloak refuses with
      // "Cannot login, credential setup required" — a dead end on the primary player
      // entry path. Sign them in with our own emailed code instead.
      if (authHint === "email" && !authCapabilities().idpPasswordless) {
        pageLogger.info(`Passwordless invite on a provider without IdP passwordless — using the email-code plane`)
        sessionStorage.removeItem(REDIRECT_FLAG)
        setEmailCodeFor(invitedEmail ?? "")
        return
      }

      pageLogger.info(`Redirecting to IdP login (authHint: ${authHint}${invitedEmail ? `, hint: ${invitedEmail}` : ""})`)
      const authParams = {
        prompt: "login",
      }
      if (authHint === "email") {
        authParams.connection = "email"
      }
      if (invitedEmail) {
        authParams.login_hint = invitedEmail
      }
      // Don't pass organization — the invite token carries the org context.
      // Passing org can cause 400 if the connection isn't enabled for that org.

      loginWithRedirect({
        authorizationParams: authParams,
        appState: { returnTo: `/invite?token=${encodeURIComponent(tokenId)}` },
      })
    }

    handleInvite()
  }, [isAuthenticated, isLoading, tokenId, loginWithRedirect, logout, getAccessTokenSilently, user, navigate, authError])

  // After an email-code sign-in there is no IdP session, so the effect above never sees
  // `isAuthenticated`. Resolve the invite here instead, authenticating with the player
  // session token that authHeadersFor now selects.
  const resolveWithPlayerSession = useCallback(
    async (session) => {
      try {
        const { result, status } = await resolveInviteToken(null, session?.userId, { tokenId })
        if (status === 200 && result?.sessionName) {
          pageLogger.info(`Invite resolved after email-code sign-in: session='${result.sessionName}'`)
          navigate(`${ROUTE_PATHS.GAME_ROOT}/${encodeRFC3986URIComponent(result.sessionName)}`)
          return
        }
        pageLogger.warn(`Invite token failed after email-code sign-in (status: ${status})`)
      } catch (err) {
        pageLogger.error(`Failed to resolve invite token after email-code sign-in: ${err}`)
      }
      // Signed in but the token would not resolve — the exercise list is still the
      // right place to land, rather than stranding them on this page.
      navigate(ROUTE_PATHS.GAME_ROOT)
    },
    [tokenId, navigate]
  )

  // Standalone: this route has no surrounding login chrome, so the component renders
  // the same dark page and white card the identity provider serves. An invitee should
  // not be able to tell which screen signed them in.
  if (emailCodeFor !== null) {
    return <EmailCodeSignIn standalone initialEmail={emailCodeFor} onAuthenticated={resolveWithPlayerSession} />
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", gap: 2 }}>
      <CircularProgress />
      <Typography variant="body1" color="text.secondary">
        Loading your training session...
      </Typography>
    </Box>
  )
}
